import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Save as SaveIcon } from "@mui/icons-material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { postCompanyUpdate } from "../services/callApi";
import {
  EXAM_PASSWORD_DUPLICATED_MESSAGE,
  getApiStatus,
  getManagementApiErrorMessage,
  isExamPasswordDuplicatedError,
} from "../utils/managementApiError";

const toFormData = (company) => ({
  companyName: company?.companyName ?? "",
  examPassword: company?.examPassword ?? "",
  isActive: company?.isActive !== false,
});

export const buildCompanyUpdatePayload = (company, formData) => ({
  company_id: company.companyId,
  company_name: formData.companyName.trim(),
  exam_password: formData.examPassword,
  is_active: formData.isActive,
});

function CompanyDetail({ company, role, onBack, onUpdated, onForbidden }) {
  const [formData, setFormData] = useState(() => toFormData(company));
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  // 試験用パスワードの重複はパスワード欄の直下に出すため、上部Alertとは別に持つ。
  const [examPasswordError, setExamPasswordError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const updateInFlightRef = useRef(false);
  const [showExamPassword, setShowExamPassword] = useState(false);
  const canDeactivate = role === "super_admin";

  useEffect(() => {
    setFormData(toFormData(company));
    setErrorMessage("");
    setSuccessMessage("");
    setExamPasswordError("");
    setShowExamPassword(false);
  }, [company]);

  const handleApiError = (error) => {
    const status = getApiStatus(error);
    if (status === 401) {
      window.location.href = "/login";
      return;
    }
    if (status === 403) {
      onForbidden("companyList");
      return;
    }
    if (isExamPasswordDuplicatedError(error)) {
      // 該当項目が分かるよう、上部Alertではなくパスワード欄の直下へ出す。
      setExamPasswordError(EXAM_PASSWORD_DUPLICATED_MESSAGE);
      return;
    }
    setErrorMessage(getManagementApiErrorMessage(error));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (updateInFlightRef.current) {
      return;
    }
    setErrorMessage("");
    setSuccessMessage("");
    setExamPasswordError("");

    if (!formData.companyName.trim() || !formData.examPassword) {
      setErrorMessage("会社名と試験用パスワードを入力してください。");
      return;
    }

    updateInFlightRef.current = true;
    setIsSubmitting(true);
    try {
      await postCompanyUpdate(buildCompanyUpdatePayload(company, formData));
      await onUpdated(company.companyId);
      setSuccessMessage("会社情報を更新しました。");
    } catch (error) {
      handleApiError(error);
    } finally {
      updateInFlightRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 0, sm: 1 } }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 1,
          mb: 2,
        }}
      >
        <Typography variant="h5" component="h1">
          会社詳細
        </Typography>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            underline="hover"
            color="inherit"
            sx={{ cursor: "pointer" }}
            onClick={(event) => {
              event.preventDefault();
              onBack();
            }}
          >
            会社一覧
          </Link>
          <Typography color="text.primary">詳細</Typography>
        </Breadcrumbs>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      <Paper sx={{ p: { xs: 2, sm: 3 } }} elevation={3}>
        <Box component="form" onSubmit={handleSubmit}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Typography sx={{ width: { xs: "40%", sm: "25%" } }}>ID</Typography>
            <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
            <Typography>{company.companyId}</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Typography sx={{ width: { xs: "40%", sm: "25%" } }}>更新日時</Typography>
            <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
            <Typography sx={{ whiteSpace: "nowrap" }}>{company.updatedAt ?? "－"}</Typography>
          </Box>
          <TextField
            fullWidth
            required
            label="会社名"
            value={formData.companyName}
            onChange={(event) =>
              setFormData((current) => ({
                ...current,
                companyName: event.target.value,
              }))
            }
            disabled={isSubmitting}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            required
            type={showExamPassword ? "text" : "password"}
            autoComplete="new-password"
            label="試験用パスワード"
            value={formData.examPassword}
            onChange={(event) => {
              setFormData((current) => ({
                ...current,
                examPassword: event.target.value,
              }));
              setExamPasswordError("");
            }}
            disabled={isSubmitting}
            error={!!examPasswordError}
            helperText={examPasswordError}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showExamPassword ? "試験用パスワードを隠す" : "試験用パスワードを表示"}
                    onClick={() => setShowExamPassword((current) => !current)}
                    edge="end"
                    disabled={isSubmitting}
                  >
                    {showExamPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          {canDeactivate && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(event) =>
                    setFormData((current) => ({
                      ...current,
                      isActive: event.target.checked,
                    }))
                  }
                  disabled={isSubmitting}
                />
              }
              label={formData.isActive ? "有効" : "無効"}
              sx={{ mb: 2 }}
            />
          )}
          <Box sx={{ textAlign: "center" }}>
            <Button
              variant="contained"
              type="submit"
              startIcon={<SaveIcon />}
              disabled={isSubmitting}
            >
              更新
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

export default CompanyDetail;
